/* ═══════════════════════════════════════════════════════════════
   The Designer — a tool opened from the MENU rather than the dock.

   Josh's law (docs/DESIGNER_UNIFIED_PLAN.md §1): the menu is the
   permanent, complete access path to every tool; docking is a
   convenience layered on top. So every palette must be fully operable
   without ever being docked — this is the surface that guarantees it.

   Portalled to <body>, position: fixed, viewport-clamped, top of the
   z-scale (DESIGN §4 "context UI always on top"). Drag it by its title
   bar, resize it from the corner, dock it with one click.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';

export interface ToolPanelProps {
  title: string;
  /** Where the panel first appears (the toolbar button that opened it). */
  anchor?: { left: number; bottom: number } | null;
  /** Exact spot to open at — where a drag-to-float released (§2.8). Wins
      over `anchor` and skips the cascade: the panel appears under the
      pointer, which is where the user just said they wanted it. */
  at?: { left: number; top: number } | null;
  /** Stacking offset so two panels opened together do not sit on top of
      each other. */
  index?: number;
  onDock: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

const PANEL_W = 360;
const PANEL_H = 460;
const CASCADE = 28;

export function ToolPanel({
  title,
  anchor,
  at,
  index = 0,
  onDock,
  onClose,
  children,
}: ToolPanelProps) {
  const clamp = React.useCallback((left: number, top: number) => {
    const maxL = Math.max(8, window.innerWidth - PANEL_W - 8);
    const maxT = Math.max(8, window.innerHeight - 120);
    return { left: Math.max(8, Math.min(left, maxL)), top: Math.max(8, Math.min(top, maxT)) };
  }, []);

  // Cascade DOWN-AND-LEFT from the right edge: each panel steps clear of the
  // one before it instead of stacking on the same clamped x and hiding it.
  const [pos, setPos] = React.useState(() =>
    at
      ? // Dropped here on purpose: open under the pointer, no cascade.
        clamp(at.left - PANEL_W / 2, at.top - 14)
      : clamp(
          (anchor?.left ?? window.innerWidth - PANEL_W - 24) - index * CASCADE,
          (anchor?.bottom ?? 96) + 8 + index * CASCADE
        )
  );

  // Esc closes — the same exit every overlay on this surface offers.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A shrinking window must never strand a panel off-screen.
  React.useEffect(() => {
    const onResize = () => setPos((p) => clamp(p.left, p.top));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const dragRef = React.useRef<{ dx: number; dy: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    // Only the title bar's empty space drags — never its buttons.
    if ((e.target as HTMLElement).closest('button')) return;
    dragRef.current = { dx: e.clientX - pos.left, dy: e.clientY - pos.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPos(clamp(e.clientX - d.dx, e.clientY - d.dy));
  };
  const endDrag = (e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
  };

  return createPortal(
    <section
      className="dsg-toolpanel"
      role="dialog"
      aria-label={title}
      style={{ left: pos.left, top: pos.top, width: PANEL_W, height: PANEL_H }}
    >
      <header
        className="dsg-toolpanel-bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span className="dsg-toolpanel-title">{title}</span>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onDock}
          aria-label={`Dock ${title} into the workspace`}
          title="Dock into the workspace"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M15 4v16" />
          </svg>
        </button>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onClose}
          aria-label={`Close ${title}`}
          title="Close"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>
      <div className="dsg-toolpanel-body">{children}</div>
    </section>,
    document.body
  );
}
